'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import Image from 'next/image';
import ProductCard from './ProductCard';
import { ProductBase } from '@/types/product';
import { BreadcrumbMenu } from '@/types/menu';
import { generateColor } from '@/utils/productMapper';


interface ProductCarouselProps {
  breadcrumbMenu: BreadcrumbMenu;
  items: ProductBase[];
  className?: string;
  onFavoriteToggle?: (id: string) => void;
  defaultVisibleCount?: number;
  showNavigation?: boolean;
  productTitleAsParagraph?: boolean;
}

const ProductCarousel: React.FC<ProductCarouselProps> = ({
  breadcrumbMenu,
  items,
  className = "",
  onFavoriteToggle,
  defaultVisibleCount = 4,
  showNavigation = false,
  productTitleAsParagraph = false,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(defaultVisibleCount);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  // Detectar dispositivo móvil de forma más simple
  useEffect(() => {
    const checkMobile = () => {
      if (typeof window !== 'undefined') {
        const isMobile = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        setIsMobileDevice(isMobile);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Determinar si el dispositivo soporta arrastre (móvil/tablet)
  const isDragEnabled = isMobileDevice;

  // Calcular cuántos productos caben en la pantalla
  const calculateVisibleCount = () => {
    if (typeof window === 'undefined') return defaultVisibleCount;

    // Si se especifica un defaultVisibleCount, usarlo directamente
    if (defaultVisibleCount && defaultVisibleCount !== 4) {
      return defaultVisibleCount;
    }

    // Usar el ancho de la ventana como fallback si el contenedor no está disponible
    const containerWidth = containerRef.current?.clientWidth || window.innerWidth;

    // Cada producto tiene 267px de ancho + 16px de gap = 283px por producto
    const productWidth = 267;
    const gap = 16;
    const totalProductWidth = productWidth + gap;

    // Calcular cuántos productos caben realmente
    const maxProducts = Math.floor((containerWidth - gap) / totalProductWidth);

    // Aplicar límites mínimos y máximos
    if (maxProducts < 1) return 1;
    if (maxProducts > 5) return 5;

    return maxProducts;
  };

  // Efecto para recalcular cuando cambie el tamaño de la ventana
  useEffect(() => {
    const updateVisibleCount = () => {
      const newCount = calculateVisibleCount();
      setVisibleCount(newCount);
    };

    // Calcular inicialmente
    updateVisibleCount();

    // Escuchar cambios de tamaño de ventana
    window.addEventListener('resize', updateVisibleCount);

    return () => {
      window.removeEventListener('resize', updateVisibleCount);
    };
  }, [defaultVisibleCount]);

  const maxIndex = Math.max(0, items.length - visibleCount);
  const shouldShowArrows = showNavigation && items.length > visibleCount;
  const totalPages = Math.ceil(items.length / visibleCount);

  // Para desktop: mostrar flechas solo en hover
  // Para móvil/tablet: mostrar según showNavigation
  const shouldShowArrowsWithHover = shouldShowArrows;


  // Constantes para el cálculo del arrastre
  const productWidth = 267;
  const gap = 16;
  const totalProductWidth = productWidth + gap;

  // Función para calcular la posición de transformación
  const getTransformX = useCallback(() => {
    const baseTransform = currentIndex * totalProductWidth;
    return baseTransform - dragOffset;
  }, [currentIndex, totalProductWidth, dragOffset]);

  // Función para ir a una página específica
  const goToPage = useCallback((pageIndex: number) => {
    setCurrentIndex(Math.min(pageIndex * visibleCount, maxIndex));
    setDragOffset(0);
  }, [visibleCount, maxIndex]);

  // Función para ir a la página anterior
  const prevSlide = useCallback(() => {
    setCurrentIndex(prev => Math.max(prev - 1, 0));
    setDragOffset(0);
  }, []);

  // Función para ir a la página siguiente
  const nextSlide = useCallback(() => {
    setCurrentIndex(prev => Math.min(prev + 1, maxIndex));
    setDragOffset(0);
  }, [maxIndex]);

  // Función para manejar el inicio del arrastre (solo en móvil/tablet)
  const handleDragStart = useCallback((clientX: number) => {
    if (!isDragEnabled) return;
    setIsDragging(true);
    setStartX(clientX);
    setCurrentX(clientX);
    setDragOffset(0);
  }, [isDragEnabled]);

  // Función para manejar el movimiento durante el arrastre (solo en móvil/tablet)
  const handleDragMove = useCallback((clientX: number) => {
    if (!isDragEnabled || !isDragging) return;

    const deltaX = clientX - startX;
    setDragOffset(deltaX);
    setCurrentX(clientX);
  }, [isDragEnabled, isDragging, startX]);

  // Función para manejar el fin del arrastre (solo en móvil/tablet)
  const handleDragEnd = useCallback(() => {
    if (!isDragEnabled || !isDragging) return;

    setIsDragging(false);

    // Calcular si el arrastre fue suficiente para cambiar de página
    const threshold = totalProductWidth * 0.3; // 30% del ancho del producto

    if (Math.abs(dragOffset) > threshold) {
      if (dragOffset > 0) {
        // Arrastre hacia la derecha (página anterior)
        prevSlide();
      } else {
        // Arrastre hacia la izquierda (página siguiente)
        nextSlide();
      }
    } else {
      // Volver a la posición original
      setDragOffset(0);
    }
  }, [isDragEnabled, isDragging, dragOffset, totalProductWidth, prevSlide, nextSlide]);

  // Eventos de mouse
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    handleDragStart(e.clientX);
  }, [handleDragStart]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    handleDragMove(e.clientX);
  }, [handleDragMove]);

  const handleMouseUp = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  // Eventos de touch
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    handleDragStart(touch.clientX);
  }, [handleDragStart]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    handleDragMove(touch.clientX);
  }, [handleDragMove]);

  const handleTouchEnd = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  // Agregar y remover event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Prevenir selección de texto durante el arrastre
  useEffect(() => {
    if (isDragging) {
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'grabbing';
    } else {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    }

    return () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isDragging]);

  const visibleItems = items.slice(currentIndex, currentIndex + visibleCount);

  return (
    <div data-component-id="src/components/ui/ProductCarousel.tsx:243:4" data-component-path="src/components/ui/ProductCarousel.tsx" data-component-line="243" data-component-file="ProductCarousel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
      className={`relative ${className}`}
      ref={containerRef}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Navigation - Previous */}
      {shouldShowArrowsWithHover && currentIndex > 0 && (
        <button data-component-id="src/components/ui/ProductCarousel.tsx:251:8" data-component-path="src/components/ui/ProductCarousel.tsx" data-component-line="251" data-component-file="ProductCarousel.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
          onClick={prevSlide}
          className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-20 bg-global-1 rounded-full w-8 h-8 flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 hover:bg-global-2 ${isDragEnabled ? 'opacity-100' : (isHovered ? 'opacity-100' : 'opacity-0')
            }`}
          style={{
            transition: 'all 0.3s ease'
          }}
          aria-label="Producto anterior"
        >
          <Image data-component-id="src/components/ui/ProductCarousel.tsx:260:10" data-component-path="src/components/ui/ProductCarousel.tsx" data-component-line="260" data-component-file="ProductCarousel.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22src%22%3A%22%2Fimages%2Fimg_arrowdown.svg%22%2C%22alt%22%3A%22Previous%22%2C%22className%22%3A%22transform%20rotate-90%20filter%20brightness-0%20invert%22%7D"
            src="/images/img_arrowdown.svg"
            alt="Previous"
            width={14}
            height={14}
            className="transform rotate-90 filter brightness-0 invert"
          />
        </button>
      )}

      {/* Carousel Container */}
      <div data-component-id="src/components/ui/ProductCarousel.tsx:271:6" data-component-path="src/components/ui/ProductCarousel.tsx" data-component-line="271" data-component-file="ProductCarousel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22overflow-hidden%20px-4%20py-4%22%7D" className="overflow-hidden px-4 py-4">
        <div data-component-id="src/components/ui/ProductCarousel.tsx:272:8" data-component-path="src/components/ui/ProductCarousel.tsx" data-component-line="272" data-component-file="ProductCarousel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
          ref={carouselRef}
          className={`flex gap-4 ${isDragEnabled && isDragging ? 'cursor-grabbing' : isDragEnabled ? 'cursor-grab' : 'cursor-default'} transition-transform duration-300 ease-out`}
          style={{
            transform: `translateX(-${getTransformX()}px)`,
            transition: isDragging ? 'none' : 'transform 0.3s ease-out'
          }}
          onMouseDown={isDragEnabled ? handleMouseDown : undefined}
          onTouchStart={isDragEnabled ? handleTouchStart : undefined}
          onTouchMove={isDragEnabled ? handleTouchMove : undefined}
          onTouchEnd={isDragEnabled ? handleTouchEnd : undefined}
        >
          {items.map((item, index) => (
            <div data-component-id="src/components/ui/ProductCarousel.tsx:285:12" data-component-path="src/components/ui/ProductCarousel.tsx" data-component-line="285" data-component-file="ProductCarousel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-shrink-0%20select-none%22%7D"
              key={item.id}
              className="flex-shrink-0 select-none"
              style={{ width: `${productWidth}px` }}
            >
              <ProductCard data-component-id="src/components/ui/ProductCarousel.tsx:290:14" data-component-path="src/components/ui/ProductCarousel.tsx" data-component-line="290" data-component-file="ProductCarousel.tsx" data-component-name="ProductCard" data-component-content="%7B%22elementName%22%3A%22ProductCard%22%7D"
                index={index}
                breadcrumbMenu={breadcrumbMenu}
                product={item}
                backgroundColor={generateColor(index) || '#FFFFFF'}
                onFavoriteToggle={onFavoriteToggle}
                productTitleAsParagraph={productTitleAsParagraph}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Navigation - Next */}
      {shouldShowArrowsWithHover && currentIndex < maxIndex && (
        <button data-component-id="src/components/ui/ProductCarousel.tsx:305:8" data-component-path="src/components/ui/ProductCarousel.tsx" data-component-line="305" data-component-file="ProductCarousel.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
          onClick={nextSlide}
          className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-20 bg-global-1 rounded-full w-8 h-8 flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 hover:bg-global-2 ${isDragEnabled ? 'opacity-100' : (isHovered ? 'opacity-100' : 'opacity-0')
            }`}
          style={{
            transition: 'all 0.3s ease'
          }}
          aria-label="Siguiente producto"
        >
          <Image data-component-id="src/components/ui/ProductCarousel.tsx:314:10" data-component-path="src/components/ui/ProductCarousel.tsx" data-component-line="314" data-component-file="ProductCarousel.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22src%22%3A%22%2Fimages%2Fimg_arrowdown.svg%22%2C%22alt%22%3A%22Next%22%2C%22className%22%3A%22transform%20-rotate-90%20filter%20brightness-0%20invert%22%7D"
            src="/images/img_arrowdown.svg"
            alt="Next"
            width={14}
            height={14}
            className="transform -rotate-90 filter brightness-0 invert"
          />
        </button>
      )}

      {/* Indicadores de navegación */}
      {shouldShowArrowsWithHover && totalPages > 1 && (
        <div data-component-id="src/components/ui/ProductCarousel.tsx:326:8" data-component-path="src/components/ui/ProductCarousel.tsx" data-component-line="326" data-component-file="ProductCarousel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`flex justify-center mt-4 gap-2 transition-opacity duration-300 ${isDragEnabled ? 'opacity-100' : isHovered ? 'opacity-100' : 'opacity-0'
          }`}>
          {Array.from({ length: totalPages }, (_, index) => (
            <button data-component-id="src/components/ui/ProductCarousel.tsx:329:12" data-component-path="src/components/ui/ProductCarousel.tsx" data-component-line="329" data-component-file="ProductCarousel.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
              key={index}
              onClick={() => goToPage(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${Math.floor(currentIndex / visibleCount) === index
                  ? 'bg-global-1 w-6'
                  : 'bg-global-2 hover:bg-global-1'
                }`}
              aria-label={`Ir a la página ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductCarousel; 